/**
 * Federn als reine Funktionen der Zeit.
 *
 * `step` ist die geschlossene Sprungantwort einer gedämpften Feder (0 → 1).
 * Ein Wert, der sein Ziel mehrfach wechselt, ist die Summe einer Feder je
 * Wechsel. Drags sind Halte-Abschnitte: Solange gehalten wird, rechnet `fn`
 * den Wert direkt aus der Zeit, beim Loslassen federt er von dort weiter.
 * Nichts wird zwischen zwei Frames gemerkt, jeder Frame ist `seek(t)`.
 */

export const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v))
export const lerp = (a, b, t) => a + (b - a) * t
export const smooth = (t) => {
  const x = clamp(t)
  return x * x * (3 - 2 * x)
}
export const easeInOut = (t) => {
  const x = clamp(t)
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2
}

// Standardfeder: setzt sich in rund 0,4 s, höchstens ein Hauch Überschwingen
export const W = 17
export const Z = 0.84

export function step(t, w = W, z = Z) {
  if (t <= 0) return 0
  const wd = w * Math.sqrt(1 - z * z)
  const e = Math.exp(-z * w * t)
  return 1 - e * (Math.cos(wd * t) + ((z * w) / wd) * Math.sin(wd * t))
}

/**
 * Zeitreihe aus Startwert und Ereignissen, sortiert nach Zeit.
 * - `{ t, to, w, z }` federt ab `t` zum Ziel `to`
 * - `{ hold: [t0, t1], fn, to }` rechnet in [t0, t1] direkt `fn(t)`,
 *   danach federt der Wert von `fn(t1)` zu `to` (Standard: dort bleiben)
 */
export function track(v0, events = []) {
  const epochs = [{ start: -Infinity, v: v0, springs: [] }]
  let cur = epochs[0]
  let target = v0
  events.forEach((e) => {
    if (e.hold) {
      const [t0, t1] = e.hold
      epochs.push({ start: t0, hold: e })
      const v = e.fn(t1)
      cur = { start: t1, v, springs: [] }
      epochs.push(cur)
      target = v
      if (e.to !== undefined && e.to !== v) {
        cur.springs.push([t1, e.to - v, e.w ?? W, e.z ?? Z])
        target = e.to
      }
      return
    }
    if (e.to === target) return
    cur.springs.push([e.t, e.to - target, e.w ?? W, e.z ?? Z])
    target = e.to
  })

  return (t) => {
    let i = epochs.length - 1
    while (i > 0 && epochs[i].start > t) i--
    const ep = epochs[i]
    if (ep.hold) return ep.hold.fn(t)
    let v = ep.v
    for (const [ts, d, w, z] of ep.springs) v += d * step(t - ts, w, z)
    return v
  }
}

/** Mehrere Zeitreihen auf einmal, z. B. für Farbkanäle oder Punkte. */
export const tracks = (v0s, eventsFor) =>
  v0s.map((v0, k) => track(v0, eventsFor(k)))

const hex = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16))

/** Farbe als drei Federn, Ereignisse mit `{ t, to: '#rrggbb' }`. */
export function colorTrack(c0, events) {
  const base = hex(c0)
  const ch = base.map((v, k) =>
    track(
      v,
      events.map((e) => ({ ...e, to: hex(e.to)[k] }))
    )
  )
  return (t) => {
    const [r, g, b] = ch.map((f) => Math.round(clamp(f(t), 0, 255)))
    return `rgb(${r},${g},${b})`
  }
}

/** Ein- und Ausblenden eines Inhalts mit eigenem Timing, 0 bis 1. */
export function presence(t, [a, b], fadeIn = 0.2, fadeOut = 0.16) {
  const inP = a === -Infinity ? 1 : smooth((t - a) / fadeIn)
  const outP = b === Infinity ? 0 : smooth((t - (b - fadeOut)) / fadeOut)
  return inP * (1 - outP)
}

/** Kurzer Druck, z. B. beim Klick: 0 → 1 → 0 um `tc`. */
export function press(t, tc, len = 0.22) {
  const x = (t - tc) / len
  if (x <= 0 || x >= 1) return 0
  return Math.sin(x * Math.PI)
}
